import { Calendar, Vote, Timer } from 'lucide-react'
import { useTickStatus } from '@/hooks/useTickStatus'
import { TickCountdown } from './TickCountdown'
import { TickProcessingIndicator } from './TickProcessingIndicator'
import { tickToGameDate, type GameDate } from './utils'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Progress } from '@/components/ui/progress'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface TickStatusProps {
  /** World ID to fetch tick status for */
  worldId: string
  /** Optional nation ID for election countdown */
  nationId?: string
  /** Display variant */
  variant?: 'compact' | 'expanded'
  /** Show election countdown if available */
  showElectionCountdown?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * Displays current tick status, countdown to next tick, and processing indicator.
 *
 * Supports two variants:
 * - compact: Single row for header/navbar (shows tick, countdown, status icon)
 * - expanded: Full card with all details (for dashboard)
 */
export function TickStatus({
  worldId,
  nationId,
  variant = 'compact',
  showElectionCountdown = true,
  className,
}: TickStatusProps) {
  const {
    currentTick,
    maxTick,
    lastTickStatus,
    lastTickCompletedAt,
    ticksUntilElection,
    isLoading,
  } = useTickStatus(worldId, nationId)

  if (isLoading) {
    return (
      <div
        className={cn(
          'animate-pulse',
          variant === 'compact' && 'h-6 w-48 bg-muted rounded',
          variant === 'expanded' && 'h-48 bg-muted rounded-lg',
          className
        )}
      />
    )
  }

  // Convert tick to in-game date
  const gameDate = tickToGameDate(currentTick)
  const isProcessing = lastTickStatus === 'processing'

  if (variant === 'compact') {
    return (
      <CompactTickStatus
        currentTick={currentTick}
        maxTick={maxTick}
        gameDate={gameDate}
        isProcessing={isProcessing}
        lastTickStatus={lastTickStatus}
        lastTickCompletedAt={lastTickCompletedAt}
        ticksUntilElection={showElectionCountdown ? ticksUntilElection : null}
        className={className}
      />
    )
  }

  return (
    <ExpandedTickStatus
      currentTick={currentTick}
      maxTick={maxTick}
      gameDate={gameDate}
      isProcessing={isProcessing}
      lastTickStatus={lastTickStatus}
      lastTickCompletedAt={lastTickCompletedAt}
      ticksUntilElection={showElectionCountdown ? ticksUntilElection : null}
      className={className}
    />
  )
}

// ============================================================================
// Compact Variant
// ============================================================================

interface CompactTickStatusProps {
  currentTick: number
  maxTick: number | null
  gameDate: GameDate
  isProcessing: boolean
  lastTickStatus: 'pending' | 'processing' | 'completed' | 'failed'
  lastTickCompletedAt: Date | null
  ticksUntilElection: number | null
  className?: string
}

function CompactTickStatus({
  currentTick,
  maxTick,
  gameDate,
  isProcessing,
  lastTickStatus,
  lastTickCompletedAt,
  ticksUntilElection,
  className,
}: CompactTickStatusProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-2 bg-muted/50 rounded-lg',
        className
      )}
    >
      {/* Tick number with game date tooltip */}
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 cursor-help">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">
              Tick {currentTick}
              {maxTick && <span className="text-muted-foreground">/{maxTick}</span>}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>Year {gameDate.year}, Q{gameDate.quarter} (Month {gameDate.month})</p>
        </TooltipContent>
      </Tooltip>

      {/* Separator */}
      <div className="h-4 w-px bg-border" />

      {/* Countdown */}
      <TickCountdown isProcessing={isProcessing} compact />

      {/* Separator */}
      <div className="h-4 w-px bg-border" />

      {/* Status indicator */}
      <TickProcessingIndicator
        status={lastTickStatus}
        lastProcessedAt={lastTickCompletedAt}
        compact
      />

      {/* Election countdown if applicable */}
      {ticksUntilElection !== null && ticksUntilElection > 0 && (
        <>
          <div className="h-4 w-px bg-border" />
          <ElectionCountdownBadge ticksUntilElection={ticksUntilElection} compact />
        </>
      )}
    </div>
  )
}

// ============================================================================
// Expanded Variant
// ============================================================================

interface ExpandedTickStatusProps {
  currentTick: number
  maxTick: number | null
  gameDate: GameDate
  isProcessing: boolean
  lastTickStatus: 'pending' | 'processing' | 'completed' | 'failed'
  lastTickCompletedAt: Date | null
  ticksUntilElection: number | null
  className?: string
}

function ExpandedTickStatus({
  currentTick,
  maxTick,
  gameDate,
  isProcessing,
  lastTickStatus,
  lastTickCompletedAt,
  ticksUntilElection,
  className,
}: ExpandedTickStatusProps) {
  // Calculate progress percentage if maxTick is known
  const progressPercent = maxTick ? (currentTick / maxTick) * 100 : null

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Timer className="h-5 w-5" />
          Current Game State
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Tick and Game Date */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-muted-foreground">Game Progress</span>
            <span className="text-sm font-medium">
              Tick {currentTick}
              {maxTick && <span className="text-muted-foreground"> of {maxTick}</span>}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
            <span>Year {gameDate.year}, Q{gameDate.quarter}</span>
            <span>Month {gameDate.month}</span>
          </div>
          {progressPercent !== null && (
            <Progress value={progressPercent} className="h-2" />
          )}
        </div>

        {/* Countdown */}
        <div className="flex items-center justify-between py-2 border-t border-b">
          <span className="text-sm text-muted-foreground">Next tick in:</span>
          <TickCountdown isProcessing={isProcessing} showIcon />
        </div>

        {/* Status */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Last tick status:</span>
          <TickProcessingIndicator
            status={lastTickStatus}
            lastProcessedAt={lastTickCompletedAt}
          />
        </div>

        {/* Election Countdown */}
        {ticksUntilElection !== null && ticksUntilElection > 0 && (
          <div className="pt-2 border-t">
            <ElectionCountdownBadge ticksUntilElection={ticksUntilElection} />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ============================================================================
// Election Countdown Badge
// ============================================================================

interface ElectionCountdownBadgeProps {
  ticksUntilElection: number
  compact?: boolean
}

function ElectionCountdownBadge({
  ticksUntilElection,
  compact = false,
}: ElectionCountdownBadgeProps) {
  // Determine urgency based on ticks remaining
  const urgencyClass = getElectionUrgencyClass(ticksUntilElection)

  if (compact) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn('flex items-center gap-1 cursor-help', urgencyClass)}>
            <Vote className="h-4 w-4" />
            <span className="text-xs font-medium">{ticksUntilElection}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>Election in {ticksUntilElection} tick{ticksUntilElection !== 1 ? 's' : ''}</p>
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <div
      className={cn(
        'flex items-center justify-between p-2 rounded-md',
        ticksUntilElection <= 2 && 'bg-purple-100 dark:bg-purple-900/30',
        ticksUntilElection <= 1 && 'animate-pulse'
      )}
    >
      <div className="flex items-center gap-2">
        <Vote className={cn('h-5 w-5', urgencyClass)} />
        <span className="text-sm font-medium">Election Approaching</span>
      </div>
      <span className={cn('text-sm font-bold', urgencyClass)}>
        {ticksUntilElection} tick{ticksUntilElection !== 1 ? 's' : ''}
      </span>
    </div>
  )
}

function getElectionUrgencyClass(ticksRemaining: number): string {
  if (ticksRemaining <= 1) return 'text-purple-600 dark:text-purple-400'
  if (ticksRemaining <= 3) return 'text-purple-500 dark:text-purple-400'
  if (ticksRemaining <= 5) return 'text-purple-400'
  return 'text-muted-foreground'
}
